/**
 * ImprestHealthCard — sidebar card on `/petty-cash` showing what fraction
 * of the float is still in the drawer + a tip on when to top up.
 *
 * Mirrors `prototype/cash/petty-cash.html ▸ .imprest` block: percent + bar +
 * float-spent / float-remaining rows + sky-toned tip footer. Pure
 * presentation; all math is integer-paise inside the component (rule §7-7).
 *
 * @see prototype/cash/petty-cash.html `.imprest` (lines ~1230–1255)
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.2
 */
import { Card, CardBody, CardHeader, ProgressBar } from "@flint/ui";
import { paiseToInr } from "../../../lib/format.ts";

export interface ImprestHealthCardProps {
  /** Imprest ceiling in paise (stringified bigint). */
  imprestPaise: string;
  /** Current running balance in paise. */
  balancePaise: string;
}

export function ImprestHealthCard({ imprestPaise, balancePaise }: ImprestHealthCardProps) {
  const imprest = BigInt(imprestPaise || "0");
  const balance = BigInt(balancePaise || "0");
  // Spent can be negative if the drawer was over-replenished — clamp at 0.
  const spent = imprest > balance ? imprest - balance : 0n;
  const remaining = imprest > 0n ? imprest - spent : 0n;

  // Integer pct then format with one decimal — matches the prototype's "64.2%".
  const remainingTenths = imprest === 0n ? 0 : Number((remaining * 1000n) / imprest);
  const spentTenths = imprest === 0n ? 0 : Number((spent * 1000n) / imprest);
  const remainingPct = remainingTenths / 10;
  const spentPct = spentTenths / 10;

  return (
    <Card>
      <CardHeader className="p-0 border-b-0">
        <h3 className="px-4 pt-3.5 text-xs font-semibold text-muted uppercase tracking-wider">
          Imprest health
        </h3>
      </CardHeader>
      <CardBody className="pt-3">
        <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">
          Float remaining
        </div>
        <div className="text-3xl font-bold tracking-tight num leading-none mb-3 flex items-baseline gap-1.5">
          <span>{remainingPct.toFixed(1)}</span>
          <span className="text-sm text-muted font-semibold">%</span>
        </div>
        <ProgressBar
          value={remainingPct}
          height={10}
          className="border border-line mb-3.5"
          aria-label={`${remainingPct.toFixed(1)} percent of imprest float remaining`}
        />
        <div className="flex flex-col gap-2 pt-1 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-muted">
              Float spent <span className="text-xs ml-1">{spentPct.toFixed(1)}%</span>
            </span>
            <span className="num font-semibold">{paiseToInr(spent)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-muted">
              Float remaining <span className="text-xs ml-1">{remainingPct.toFixed(1)}%</span>
            </span>
            <span className="num font-semibold">{paiseToInr(remaining)}</span>
          </div>
        </div>
        <p className="mt-3 px-2.5 py-2 bg-sky-50 border border-sky-100 rounded-sm text-xs text-sky-700 leading-relaxed">
          Top up when the float drops below 30% to keep audit clean (ADR-0012 §10.2).
        </p>
      </CardBody>
    </Card>
  );
}
